import API from "../Services/API";
import Model from "./Model";
import State from "./State";

const useBroadcasts = (alwaysRefresh) => {
    return Model({
        alwaysRefresh,
        stateKey: ['broadcasts'],
        fetchData: () => API.get('/broadcasts'),
        returnDataKey: 'broadcasts',
        saveAdditionalData: (response) => {
            response.map((broadcast) => {
                State.putIfNotExists(['broadcast', broadcast.id], broadcast);
                return broadcast;
            });
        },
        registerAdditionalMethods: (opts) => {
            return {
                addBroadcast: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/broadcasts', data),
                        'Broadcast added',
                        true,
                    );
                },
            }
        },
        defaultLoadingError: 'Failed to load broadcasts.',
    });
}


const useBroadcast = (broadcastId, alwaysRefresh) => {
    return Model({
        alwaysRefresh,
        stateKey: ['broadcast', broadcastId],
        fetchData: () => API.get(`/broadcasts/${broadcastId}`),
        returnDataKey: 'broadcast',
        subscribeToUpdates: {
            entity: {entityModel: 'Broadcasts/Broadcast', entityId: broadcastId},
            handler: (data, opts) => {
                console.log('Broadcast updated', data);
                opts.refresh();
            },
        },
        registerAdditionalMethods: (opts) => {
            return {
                update: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/broadcasts/'+broadcastId, data),
                        'Broadcast updated',
                        true,
                    );
                },
                selectRecipients: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/broadcasts/${broadcastId}/recipients`, data),
                        'Broadcast contacts updated',
                        true,
                    );
                },
                removeRecipients: (recipientIds) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/broadcasts/${broadcastId}/recipients`, {recipientIds}),
                        'Broadcast contacts removed',
                        true,
                    );
                },
                send: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/broadcasts/${broadcastId}/send`, data),
                        'Broadcast was started',
                        true,
                    );
                },
                duplicate: (duplicateId) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/broadcasts/${broadcastId}/duplicate`, {duplicateId}),
                        'Broadcast has been copied',
                        true,
                    );
                },
                archive: () => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/broadcasts/${broadcastId}`),
                        'Broadcast has been archived',
                        true,
                    );
                },
            }
        },
        defaultLoadingError: 'Failed to load broadcast.',
    });
}

const broadcastIcons = {
    'Draft': 'Pencil',
    'Ready': 'PlayPause',
    'Scheduled': 'Click',
    'Sending': 'Loading',
    'Sent': 'Check',
    'Archived': 'Archive',
}

const recipientStatuses = {
    'Inactive': {
        icon: 'Ban',
        type: 'Unavailable',
        description: 'The customer does not have an active jobs',
    },
    'Unreachable': {
        icon: 'SquareQuestion',
        type: 'Unavailable',
        description: 'The customer can\'t be reached using the selected channels',
    },
    'Opted-Out': {
        icon: 'Stop',
        type: 'Unavailable',
        description: 'The customer has opted out of receiving promotional messages',
    },
    'Ready': {
        icon: 'PlayPause',
        type: 'Pending',
        description: 'The customer is ready to receive the message',
    },
    'Sending': {
        icon: 'Play',
        type: 'Pending',
        description: 'The message is being sent to the customer',
    },
    'Sent': {
        icon: 'Check',
        type: 'Successful',
        description: 'The message has been sent to the customer',
    },
    'Failed': {
        icon: 'XMark',
        type: 'Error',
        description: 'The message failed to send to the customer',
    },
    'Delivered': {
        icon: 'CheckDouble',
        type: 'Successful',
        description: 'The message has been delivered to the customer',
    },
    'Opened': {
        icon: 'EnvelopeOpen',
        type: 'Successful',
        description: 'The message has been opened by the customer',
    },
    'Clicked': {
        icon: 'HandPointer',
        type: 'Successful',
        description: 'The customer has clicked a link in the message',
    },
    'Unsubscribed': {
        icon: 'Ban',
        type: 'Error',
        description: 'The customer has unsubscribed from further messages',
    },
    'Complained': {
        icon: 'Ban',
        type: 'Error',
        description: 'The customer has complained about the message (reported as spam)',
    },
    'Unknown': {
        icon: 'Question',
        type: 'Error',
        description: 'The status of the message is unknown',
    },
}

const typeClasses = {
    'Unavailable': {
        border: 'border-amber-600',
        bgLight: 'bg-amber-50',
        bg: 'bg-amber-600',
        fg: 'text-amber-600 fill-amber-600',
    },
    'Pending': {
        border: 'border-brand',
        bgLight: 'bg-slate-50',
        bg: 'bg-brand',
        fg: 'text-brand fill-brand',
    },
    'Successful': {
        border: 'border-green-600',
        bgLight: 'bg-green-50',
        bg: 'bg-green-600',
        fg: 'text-green-600 fill-green-600',
    },
    'Error': {
        border: 'border-red-600',
        bgLight: 'bg-red-50',
        bg: 'bg-red-600',
        fg: 'text-red-600 fill-red-600',
    },
    'Unknown': {
        border: 'border-gray-600',
        bgLight: 'bg-gray-50',
        bg: 'bg-gray-600',
        fg: 'text-gray-600 fill-gray-600',
    },

}

const groupStatsByType = (stats) => {
    return Object.keys(stats).reduce((acc, key) => {
        const type = recipientStatuses[key]?.type ?? 'Unknown';
        acc[type] = (acc[type] ?? 0) + stats[key];
        return acc;
    }, {});
}

export {
    useBroadcasts,
    useBroadcast,
    broadcastIcons,
    recipientStatuses,
    groupStatsByType,
    typeClasses,
}